import { useEffect, useState } from 'react'
import { XIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { Alert } from './inspectors/Alert'
import { Section } from './inspectors/Section'
import { Field, TextField, ToggleField } from './inspectors/fields'
import { type GlobalWorkflowSettings, sanitizeGlobalSettings } from 'app/lib/workflows/globalSettings'

type GlobalSettingsModalProps = {
  initial: GlobalWorkflowSettings
  /** Persist the settings. Resolve to null on success, or a user-readable error message —
   *  the modal stays open and shows it, so a failed save never eats the user's input. */
  onSave: (settings: GlobalWorkflowSettings) => Promise<string | null>
  onClose: () => void
}

type CapRowState = {
  enabled: boolean
  setEnabled: (on: boolean) => void
  text: string
  setText: (value: string) => void
  value: number
  valid: boolean
}

/** `floor` is the smallest value this row accepts. Caps allow 0, which the engine treats as "send
 *  nothing and hold the runs" rather than "no cap" (api #519). The row already has its own on/off
 *  toggle, so off and zero are separate answers and neither has to be inferred from a number. The
 *  enrollment pace keeps a floor of 1: zero there would stall enrolment with nothing to release it. */
function useCapRow(initial: number | undefined, fallback: number, floor = 0): CapRowState {
  const [enabled, setEnabled] = useState(initial != null)
  const [text, setText] = useState(String(initial ?? fallback))
  const value = Math.floor(Number(text))
  const valid = !enabled || (Number.isFinite(value) && value >= floor)
  return { enabled, setEnabled, text, setText, value, valid }
}

/** Automation settings for the Workflows main page: workspace-wide outbound caps and enrollment
 *  pacing that govern every workflow at once. These persist on the Workspace and the engine
 *  enforces them — runs that hit a cap hold and auto-resume when the window resets. */
export function GlobalSettingsModal({ initial, onSave, onClose }: GlobalSettingsModalProps) {
  const monthly = useCapRow(initial.monthlyCap, 500)
  const daily = useCapRow(initial.dailyCap, 200)
  const pace = useCapRow(initial.enrollmentsPerHour, 50, 1)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === `Escape`) onClose()
    }
    window.addEventListener(`keydown`, onKey)
    return () => window.removeEventListener(`keydown`, onKey)
  }, [onClose])

  const allValid = monthly.valid && daily.valid && pace.valid

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    const error = await onSave(
      sanitizeGlobalSettings({
        monthlyCap: monthly.enabled ? monthly.value : undefined,
        dailyCap: daily.enabled ? daily.value : undefined,
        enrollmentsPerHour: pace.enabled ? pace.value : undefined,
      }),
    )
    setSaving(false)
    if (error) setSaveError(error)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-surface-raised shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-default px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-strong">Automation settings</h2>
            <p className="text-xs text-subtle">Volume and pacing guardrails, applied to every workflow.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-7 items-center justify-center rounded-md text-subtle hover:bg-surface-muted"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <Alert tone="info" lead="Enforced by the engine">
            A workflow that hits a cap pauses at the send and continues automatically when the window resets, so
            contacts stay in their sequence. Set limits before turning campaigns on: changes made while automations are
            running may not take effect right away.
          </Alert>

          <Section title="Volume">
            <ToggleField
              label="Cap monthly outbound volume"
              info="Total SMS, calls, and notifications all workflows may create in one calendar month. Set it to 0 to pause all sending: nothing goes out and the runs wait rather than being dropped."
              checked={monthly.enabled}
              onChange={monthly.setEnabled}
            />
            {monthly.enabled && (
              <Field
                label="Max outbound actions per month"
                error={monthly.valid ? undefined : `Enter a whole number, 0 or more`}
              >
                <TextField
                  type="number"
                  min={0}
                  step={1}
                  value={monthly.text}
                  invalid={!monthly.valid}
                  onChange={(e) => monthly.setText(e.target.value)}
                />
              </Field>
            )}
            <ToggleField
              label="Cap daily outbound volume"
              info="The same budget per day: stops a runaway workflow from spending a month in an hour. 0 pauses sending until tomorrow."
              checked={daily.enabled}
              onChange={daily.setEnabled}
            />
            {daily.enabled && (
              <Field
                label="Max outbound actions per day"
                error={daily.valid ? undefined : `Enter a whole number, 0 or more`}
              >
                <TextField
                  type="number"
                  min={0}
                  step={1}
                  value={daily.text}
                  invalid={!daily.valid}
                  onChange={(e) => daily.setText(e.target.value)}
                />
              </Field>
            )}
          </Section>

          <Section title="Pacing">
            <ToggleField
              label="Pace enrollment processing"
              info="Drip newly enrolled contacts through their first step instead of all at once."
              checked={pace.enabled}
              onChange={pace.setEnabled}
            />
            {pace.enabled && (
              <Field
                label="Enrollments per hour"
                hint={
                  pace.valid
                    ? `Enroll 500 at ${pace.value} an hour and the queue drains over ${Math.ceil(500 / pace.value)} hours.`
                    : undefined
                }
                error={pace.valid ? undefined : `Enter a whole number of 1 or more`}
              >
                <TextField
                  type="number"
                  min={1}
                  step={1}
                  value={pace.text}
                  invalid={!pace.valid}
                  onChange={(e) => pace.setText(e.target.value)}
                  className="w-24"
                />
              </Field>
            )}
          </Section>

          {saveError && (
            <Alert tone="warn" lead="Couldn't save">
              {saveError}
            </Alert>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-default px-4 py-3">
          <Button variant="white" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!allValid || saving}
            onClick={() => {
              handleSave().catch(() => setSaving(false))
            }}
          >
            {saving ? `Saving…` : `Save settings`}
          </Button>
        </footer>
      </div>
    </div>
  )
}
